import {
  CheckCircleOutlined,
  EditOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Empty,
  Image,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { MenuProps } from 'antd/es/menu';
import { cardTypeLabel } from 'apps/investment/constants/user';
import UIIconTooltip from 'common/ui/UIIconTooltip';
import { useFetchApiGet, useFetchApiList, usePostApi } from 'hooks/api';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import IdentificationPreviewModal from './identification-preview-modal';
import IndividualIdentificationDetail from './individual-identification-detail';

import companyApiCall from 'apps/futureflex/apiCalls/company';
import employeeApiCall from 'apps/futureflex/apiCalls/employee';
import apiFundEcontractsCall from 'apps/investment/apiCalls/fund-econtracts/list';
import listFundAccountApiCall from 'apps/investment/apiCalls/fundAccount/list';
import stockBalanceHistoriesApiCall from 'apps/investment/apiCalls/stock-balance-histories';
import apiCall from 'apps/investment/apiCalls/user';
import apiUserIdentificationLogsCall from 'apps/investment/apiCalls/user/user-identification-logs';
import { columnsGenerator as columnsGeneratorFundEcontracts } from 'apps/investment/components/fund-econtracts/config';
import { columnsGenerator as columnsGeneratorFundAccount } from 'apps/investment/components/FundAccount/config';
import { identificationStatusColors } from 'apps/investment/constants/user';
import { DataTable } from 'common/data-table';
import OnlineTooltip from 'common/online-tooltip';
import ErrorMessage from 'common/ui/ErrorMessage';
import { Label } from 'common/ui/label';
import {
  investmentIdentificationImageStatusColors,
  investmentIdentificationImageStatusLabel,
  investmentIdentificationImageTypeLabels,
} from 'utils/constants';
import { formatDateTime } from 'utils/formatDate';
import { columnsStockAccount, columnsStockBalanceHistories } from './config';
import FieldWarning from './field-warning';
import { AutoKycResponse } from './types';

const { Item: DescItem } = Descriptions;
const { Title } = Typography;

const column = {
  xxl: 2,
  xl: 2,
  lg: 2,
  md: 1,
  sm: 1,
  xs: 1,
};

const AUTO_KYC_LOADING_MSG_KEY = 'autoKycLoading';

const USER_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'Archived', value: 'archived' },
  { label: 'Deleted', value: 'deleted' },
];

const USER_STATUS_COLORS: Record<string, string> = {
  active: 'green',
  blocked: 'red',
  archived: 'orange',
  deleted: 'default',
};

interface ItemDetailProps {
  item: any;
  onRefresh?: () => void;
}

const ItemDetail = ({ item, onRefresh }: ItemDetailProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const {
    identification,
    code,
    email,
    id,
    name,
    email_verified,
    ekyc_verified,
    re_kyc,
    phone,
    phone_verified,
    updated_at,
    created_at,
    is_online,
    last_login_at,
    account_type,
    status,
  } = item || {};

  // eKYC approve/reject and status changes both mutate the user record the
  // parent DetailPage owns and the sub-queries below consume, so refresh
  // all three tiers together.
  const refreshUserData = () => {
    onRefresh?.();
    queryClient.invalidateQueries({ queryKey: [apiCall.checkKyc.queryKey] });
    queryClient.invalidateQueries({ queryKey: [apiCall.IDImage.queryKey] });
  };

  const {
    post: onSetStatus,
    isLoading: isSettingStatus,
  } = usePostApi(apiCall.setStatus, { onSuccess: refreshUserData });

  const {
    post: onApprove,
    isLoading: isApproving,
    error: approveError,
  } = usePostApi(apiCall.approve, { onSuccess: refreshUserData });

  const {
    post: onReject,
    isLoading: isRejecting,
    error: rejectError,
  } = usePostApi(apiCall.reject, { onSuccess: refreshUserData });

  const {
    data: IDImage,
    load: loadIDImage,
    isLoading: isLoadingIDImage,
    error: loadIDImageError,
  } = useFetchApiList(apiCall.IDImage, { resourceName: 'data' });

  const {
    data: autoKyc,
    load: loadAutoKyc,
    isLoading: isLoadingAutoKyc,
    error: loadAutoKycError,
  } = useFetchApiGet<AutoKycResponse>(apiCall.checkKyc, {
    resourceName: 'data',
  });

  const {
    data: requiredFundAccounts,
    load: loadRequiredFundAccounts,
    isLoading: isLoadingRequiredFundAccounts,
    error: loadRequiredFundAccountsError,
  } = useFetchApiList(employeeApiCall.requiredFundAccounts, {
    resourceName: 'data',
  });

  const {
    data: userEmployees,
    load: loadUserEmployees,
    isLoading: isLoadingUserEmployees,
    error: loadUserEmployeesError,
  } = useFetchApiList(employeeApiCall.list, { resourceName: 'data' });

  const {
    data: userCompanies,
    load: loadUserCompanies,
    isLoading: isLoadingUserCompanies,
    error: loadUserCompaniesError,
  } = useFetchApiList(companyApiCall.list, { resourceName: 'data' });

  const {
    data: stockBalanceHistories,
    isLoading: isLoadingStockBalanceHistories,
    error: errorStockBalanceHistories,
    load: loadStockBalanceHistories,
  } = useFetchApiList(stockBalanceHistoriesApiCall.list, {
    resourceName: 'data',
  });

  const {
    data: userIdentificationLogs,
    error: errorUserIdentificationLogs,
    load: loadUserIdentificationLogs,
  } = useFetchApiList(apiUserIdentificationLogsCall, {
    resourceName: 'data',
  });

  const {
    data: fundAccounts,
    error: errorFundAccounts,
    load: loadFundAccounts,
  } = useFetchApiList(listFundAccountApiCall, {
    resourceName: 'data',
  });

  const {
    data: stockAccount,
    load: loadStockAccount,
    error: errorStockAccount,
  } = useFetchApiGet(apiCall.stockAccount, { resourceName: 'data' });

  const {
    data: fundEcontracts,
    error: errorFundEcontracts,
    load: loadFundEcontracts,
  } = useFetchApiList(apiFundEcontractsCall, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (id) {
      loadIDImage({ id });
      if (!ekyc_verified) {
        loadAutoKyc({ id });
      }
      loadStockAccount({ id });
      loadRequiredFundAccounts({ id });
      loadUserEmployees({ user_id: id });
      loadUserCompanies({ user_id: id });
      loadStockBalanceHistories({ user_id: id });
      loadUserIdentificationLogs({ user_id: id });
      loadFundAccounts({ user_id: id });
      loadFundEcontracts({ user_id: id });
    }
  }, [id]);

  useEffect(() => {
    if (isLoadingAutoKyc) {
      messageApi.open({
        key: AUTO_KYC_LOADING_MSG_KEY,
        type: 'loading',
        content: 'Đang tải dữ liệu so sánh eKYC...',
        duration: 0,
      });
    } else {
      messageApi.destroy(AUTO_KYC_LOADING_MSG_KEY);
    }
  }, [isLoadingAutoKyc, messageApi]);

  const { id: ID_id } = identification || {};
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any | null>(null);

  const menuItems: MenuProps['items'] = [
    {
      key: '1',
      label: 'Cập nhật tài khoản',
      onClick: () => navigate(`/investment/users/${item.id}/edit`),
    },
    {
      key: '2',
      label: 'Cập nhật danh tính',
      onClick: () =>
        navigate(
          `/investment/users/${item.id}/identifications/${item.account_type}/${ID_id}/edit`
        ),
    },
    {
      key: '3',
      label: 'Xem ảnh định danh',
      onClick: () =>
        navigate(`/investment/users/${item.id}/identification-images`),
    },
    {
      key: '4',
      label: 'Xem lịch sử nạp rút',
      onClick: () =>
        navigate(`/investment/stock-balance-histories?user_id=${id}`),
    },
    {
      key: '5',
      label: 'Xem lịch sử xác thực eKYC',
      onClick: () => navigate(`/investment/users/${id}/user-ekyc-records`),
    },
    {
      key: '6',
      label: 'Xem hợp đồng quỹ mở',
      onClick: () => navigate(`/investment/fund-econtracts?user_id=${id}`),
    },
    {
      key: '7',
      label: 'Xem lịch sử khảo sát',
      onClick: () =>
        navigate(`/investment/user-survey-histories?user_id=${item.id}`),
    },
    {
      key: '9',
      label: 'Xem Danh Sách Mục Tiêu',
      onClick: () =>
        navigate(`/investment/user-programs?user_id=${item.id}`),
    },
    {
      key: '8',
      label: 'REJECT EKYC',
      disabled:
        identification?.verification_status === 'rejected' || isRejecting,
      danger: true,
      onClick: () => {
        let reason = prompt('Please enter reason for rejecting EKYC:');
        if (!reason) {
          alert('Reason for rejecting EKYC is required');
          return;
        }
        onReject({ id: item.id, payload: { reason: reason } });
      },
    },
  ];

  const handleSetStatus = () => {
    if (!selectedStatus) return;
    onSetStatus(
      { id: String(id), status: selectedStatus },
      {
        onSuccess: () => {
          message.success(`User status updated to "${selectedStatus}"`);
          setStatusModalOpen(false);
        },
        onError: (err: any) => {
          message.error(err?.message || 'Failed to update status');
        },
      }
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Change User Status"
        open={statusModalOpen}
        onCancel={() => setStatusModalOpen(false)}
        onOk={handleSetStatus}
        okText="Confirm"
        okButtonProps={{ loading: isSettingStatus, danger: selectedStatus !== 'active' }}
        destroyOnClose
      >
        <div className="space-y-3 py-2">
          <p className="text-sm text-gray-600">
            Current status: <Tag color={USER_STATUS_COLORS[status] || 'default'}>{status || 'active'}</Tag>
          </p>
          <Select
            className="w-full"
            placeholder="Select new status"
            options={USER_STATUS_OPTIONS}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
          {selectedStatus === 'blocked' && (
            <p className="text-sm text-red-500">
              Blocking will immediately prevent this user from logging in and will force logout any active sessions.
            </p>
          )}
        </div>
      </Modal>
      <div className="space-y-4">
        <div className="flex md:gap-4 gap-2 items-center flex-wrap justify-end w-full">
          <Button
            onClick={() => {
              setSelectedStatus(status || 'active');
              setStatusModalOpen(true);
            }}
            danger={status && status !== 'active'}
          >
            Status: <Tag className="ml-1" color={USER_STATUS_COLORS[status] || 'green'}>{status || 'active'}</Tag>
          </Button>
          {!item.ekyc_verified && onApprove ? (
            <Button
              disabled={isApproving}
              onClick={() => {
                let reason = prompt('Please enter note for approve EKYC:');
                if (!reason) {
                  alert('Reason for approve EKYC is required');
                  return;
                }
                onApprove({ id: item.id, payload: { reason: reason } });
              }}
              type="primary"
              style={{
                backgroundColor: 'green',
                borderColor: 'green',
              }}
            >
              Approve EKYC
            </Button>
          ) : null}

          <Link to={`/investment/stock-orders?user_id=${item.id}`}>
            <Button>Xem Lệnh CK</Button>
          </Link>

          <Link to={`/investment/fund-orders?user_id=${item.id}`}>
            <Button>Xem Lệnh Quỹ</Button>
          </Link>
          <Link to={`/robo-advisory/portfolios/${item.id}`}>
            <Button>Xem Danh Mục</Button>
          </Link>
          <div>
            <Dropdown menu={{ items: menuItems }}>
              <Button>
                <EllipsisOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
        <Card
          size="small"
          title="Tài khoản Stag"
          extra={
            <Link to={`/investment/users/${item.id}/edit`}>
              <Button>Edit</Button>
            </Link>
          }
        >
          <Descriptions column={column} bordered size="small">
            <DescItem label="Tên tài khoản">{name}</DescItem>
            <DescItem label="Email">
              <Space align="center" size="small">
                <OnlineTooltip
                  isOnline={is_online}
                  lastLoginAt={last_login_at}
                />
                {email}
                {email_verified ? <VerifiedTooltip /> : null}
              </Space>
            </DescItem>
            <DescItem label="Trạng thái eKYC">
              {ekyc_verified ? (
                <Tag color="green">Đã xác thực</Tag>
              ) : (
                <Tag color="red">Chưa xác thực</Tag>
              )}
            </DescItem>
            <DescItem label="Số điện thoại">
              <Space align="center">
                {phone}
                {phone_verified ? <VerifiedTooltip /> : null}
              </Space>
            </DescItem>
            <DescItem label="Stag ID">{code}</DescItem>
            <DescItem label="Type">{account_type}</DescItem>
            <DescItem label="Status">
              <Tag color={USER_STATUS_COLORS[status] || 'green'}>{status || 'active'}</Tag>
            </DescItem>
            <DescItem label="Thời gian tạo">
              {formatDateTime(created_at)}
            </DescItem>
            <DescItem label="Thời gian cập nhật">
              {formatDateTime(updated_at)}
            </DescItem>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={
            <div className="flex flex-wrap items-center gap-1">
              <span>Xác thực danh tính</span>
              {isLoadingAutoKyc ? null : loadAutoKycError ? (
                <div>
                  <Tag color="orange">
                    {'Lỗi Auto Approver: ' + (loadAutoKycError as any).error}
                  </Tag>
                </div>
              ) : autoKyc?.total_message > 0 ? (
                <div className="flex flex-wrap gap-1 items-center">
                  <Tag color="orange">
                    Phát hiện {autoKyc?.total_message} bất thường
                  </Tag>
                  {autoKyc?.field_warnings &&
                  'face_matching' in autoKyc?.field_warnings ? (
                    <Tag color="orange">
                      {autoKyc?.field_warnings['face_matching']}
                    </Tag>
                  ) : null}
                </div>
              ) : null}
            </div>
          }
          extra={
            <div className="flex flex-shrink gap-1 flex-wrap">
              {ID_id ? (
                <>
                  {re_kyc && ekyc_verified ? (
                    <Link
                      to={`/investment/users/${item.id}/identifications/${item.account_type}/${ID_id}/re-kyc`}
                    >
                      <Button
                        type="primary"
                        style={{
                          backgroundColor: 'purple',
                          borderColor: 'purple',
                        }}
                      >
                        Review Re-KYC
                      </Button>
                    </Link>
                  ) : null}

                  <Link
                    to={`/investment/users/${item.id}/identifications/${item.account_type}/${ID_id}/edit`}
                  >
                    <Button>Edit</Button>
                  </Link>
                </>
              ) : (
                <Link
                  to={`/investment/users/${item.id}/identifications/${account_type}/create`}
                >
                  <Button type="primary">Create</Button>
                </Link>
              )}
            </div>
          }
        >
          <div className="relative space-y-4">
            {item.account_type == 'individual' ? (
              <div className="flex gap-4 lg:flex-row flex-col">
                <IndividualIdentificationDetail
                  userIdentification={identification}
                  autoKyc={autoKyc}
                />
              </div>
            ) : (
              <BusinessIdentificationDetail
                userIdentification={identification}
              />
            )}

            <Descriptions
              size="small"
              title={'Thông tin ngân hàng'}
              column={1}
              bordered
            >
              <DescItem label="Tên ngân hàng">
                {identification?.bank ? (
                  <Link to={`/catalog/banks/${identification.bank?.id}`}>
                    {`${identification?.bank_name} (${identification.bank?.code})`}
                  </Link>
                ) : identification?.bank_name ? (
                  identification.bank_name
                ) : (
                  '-'
                )}
              </DescItem>
              <DescItem label="Chủ tài khoản">
                {identification?.bank_account_name}
                {autoKyc?.field_warnings &&
                'bank_account_name' in autoKyc?.field_warnings ? (
                  <FieldWarning
                    message={autoKyc?.field_warnings['bank_account_name']}
                  />
                ) : null}
              </DescItem>
              <DescItem label="Số tài khoản">
                {identification?.bank_account_number}
                {autoKyc?.field_warnings &&
                'bank_account_number' in autoKyc?.field_warnings ? (
                  <FieldWarning
                    message={autoKyc?.field_warnings['bank_account_number']}
                  />
                ) : null}
              </DescItem>
              <DescItem label="Chi nhánh">
                {identification?.bank_branch_name}
              </DescItem>
            </Descriptions>

            <Card size="small" title="Ghi chú">
              <div className="max-h-[21vh] overflow-y-auto">
                {errorUserIdentificationLogs ? (
                  <ErrorMessage errors={errorUserIdentificationLogs} />
                ) : userIdentificationLogs?.length > 0 ? (
                  userIdentificationLogs.map((log, index) => (
                    <p key={index}>
                      <Tag color="green">{log.admin_email ?? 'Admin'}</Tag>{' '}
                      chuyển sang &nbsp;
                      <Tag
                        color={
                          identificationStatusColors[log.verification_status]
                        }
                      >
                        {log.verification_status_label ||
                          log.verification_status}
                      </Tag>
                      (Ghi chú: &nbsp;
                      <Tag color="grey">{log.note}</Tag>
                      {formatDateTime(log.created_at)})
                    </p>
                  ))
                ) : (
                  'Chưa có ghi chú'
                )}
              </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {isLoadingIDImage ? (
                [1, 2, 3].map(item => (
                  <div
                    key={item}
                    className="w-full h-48 flex items-center justify-center bg-gray-100 overflow-hidden"
                  >
                    <Spin size="large" />
                  </div>
                ))
              ) : loadIDImageError ? (
                <div className="w-full h-48 flex items-center justify-center bg-gray-100 overflow-hidden">
                  <ErrorMessage errors={loadIDImageError} />
                </div>
              ) : IDImage?.length > 0 ? (
                <>
                  {[...IDImage].map(
                    ({
                      id,
                      data,
                      image_url,
                      image_type,
                      status,
                      error_message,
                    }) => {
                      const imageBase64 = `data:image/jpeg;base64,${data}`;
                      const previewSrc = image_url || imageBase64;
                      const isSelfie = image_type === 'selfie';
                      return (
                        <div
                          key={id}
                          className="w-full h-48 flex items-center justify-center bg-gray-100 overflow-hidden relative"
                        >
                          <Image
                            className={`w-full h-full object-contain cursor-pointer ${
                              isSelfie ? 'blur-xl scale-50' : 'scale-90'
                            }`}
                            src={previewSrc}
                            preview={false}
                            onClick={() => {
                              setSelectedImage({
                                id,
                                data,
                                image_url,
                                image_type,
                                status,
                                error_message,
                              });
                              setIsPreviewModalOpen(true);
                            }}
                          />

                          <div className="absolute top-0 left-0 right-0 px-3 py-2 bg-black/50 backdrop-blur-sm flex justify-between items-center">
                            <Label className="text-white m-0">
                              {
                                investmentIdentificationImageTypeLabels[
                                  image_type
                                ]
                              }
                            </Label>
                            <Tag
                              color={
                                investmentIdentificationImageStatusColors[
                                  status
                                ]
                              }
                            >
                              {investmentIdentificationImageStatusLabel[
                                status
                              ] || status}
                            </Tag>
                          </div>
                          {error_message !== '' && (
                            <div className="absolute bottom-0 right-0 px-3 py-2 bg-black/50 backdrop-blur-sm">
                              <Tag color="purple">{error_message}</Tag>
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </>
              ) : null}
            </div>
          </div>
        </Card>

        <Card size="small" title="Tài khoản Chứng khoán">
          {!!errorStockAccount ? (
            <ErrorMessage errors={errorStockAccount} />
          ) : (
            <Table
              className="w-full overflow-x-auto"
              dataSource={stockAccount && stockAccount.id ? [stockAccount] : []}
              columns={columnsStockAccount()}
              locale={{
                emptyText: (
                  <Empty description="Chưa yêu cầu mở tài khoản chứng khoán" />
                ),
              }}
              pagination={false}
            />
          )}
        </Card>
        <Card size="small" title="Tài khoản Quỹ">
          {!!errorFundAccounts ? (
            <ErrorMessage errors={errorFundAccounts} />
          ) : (
            <DataTable
              noViewOptions
              columns={columnsGeneratorFundAccount()}
              data={fundAccounts || []}
            />
          )}
        </Card>

        <Card size="small" title="Lịch sử nạp rút">
          {!!errorStockBalanceHistories ? (
            <ErrorMessage errors={errorStockBalanceHistories} />
          ) : (
            <>
              <DataTable
                noViewOptions
                containerClassName="max-h-[21vh]"
                columns={columnsStockBalanceHistories()}
                data={stockBalanceHistories || []}
              />
              <div className="flex justify-center text-base font-medium py-1">
                <Link to={`/investment/stock-balance-histories?user_id=${id}`}>
                  Xem tất cả
                </Link>
              </div>
            </>
          )}
        </Card>

        <Card size="small" title={'Hợp đồng quỹ mở'}>
          {!!errorFundEcontracts ? (
            <ErrorMessage errors={errorFundEcontracts} />
          ) : (
            <>
              <DataTable
                noViewOptions
                containerClassName="max-h-[21vh]"
                columns={columnsGeneratorFundEcontracts()}
                data={fundEcontracts || []}
              />
              <div className="flex justify-center text-base font-medium py-1">
                <Link to={`/investment/fund-econtracts?user_id=${id}`}>
                  Xem tất cả hđ
                </Link>
              </div>
            </>
          )}
        </Card>

        {item.account_type == 'individual' ? (
          <Card size="small" title="Nhân viên">
            {userEmployees && userEmployees.length > 0 ? (
              <Descriptions column={1} bordered>
                {userEmployees.map(userEmployee => (
                  <DescItem
                    label={
                      userEmployee.full_name
                        ? userEmployee.full_name
                        : userEmployee.email
                    }
                  >
                    <Link to={`/futureflex/employees/${userEmployee.id}`}>
                      Xem chi tiết #{userEmployee.id}
                    </Link>
                  </DescItem>
                ))}
              </Descriptions>
            ) : (
              <Empty description="Chưa tham gia chương trình FutureFlex dành cho nhân viên" />
            )}
          </Card>
        ) : (
          <Card size="small" title="Doanh nghiệp">
            {userCompanies && userCompanies.length > 0 ? (
              <Descriptions column={2} bordered>
                {userCompanies.map(userCompany => (
                  <>
                    <DescItem label={userCompany.name}>
                      <Link to={`/futureflex/companies/${userCompany.id}`}>
                        Xem chi tiết #{userCompany.id}
                      </Link>
                    </DescItem>
                    <DescItem label={'HR Admin'}>
                      <Link
                        to={`/futureflex/staffs?company_id=${userCompany.id}`}
                      >
                        Xem danh sách #{userCompany.id}
                      </Link>
                    </DescItem>
                  </>
                ))}
              </Descriptions>
            ) : (
              <Empty description="Chưa tham gia chương trình FutureFlex dành cho doanh nghiệp" />
            )}
          </Card>
        )}

        {item.account_type == 'individual' &&
        userEmployees &&
        userEmployees.length > 0 ? (
          <Card size="small" title="Tài khoản Quỹ cần mở">
            {requiredFundAccounts && requiredFundAccounts.length > 0 ? (
              <Descriptions column={1} bordered>
                {requiredFundAccounts.map(requiredFundAccount => (
                  <DescItem
                    label={`${
                      requiredFundAccount.fund_manager_name
                    } (${requiredFundAccount.fund_manager.toUpperCase()})`}
                  >
                    {requiredFundAccount.is_approved ? (
                      <Tooltip title="Đã duyệt">
                        <CheckCircleOutlined
                          style={{ color: 'green', fontSize: '20px' }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Chưa tạo hoặc chưa duyệt">
                        <InfoCircleOutlined
                          style={{ color: '#b8860b', fontSize: '20px' }}
                        />
                      </Tooltip>
                    )}
                  </DescItem>
                ))}
              </Descriptions>
            ) : (
              <Empty description="Không có tài khoản quỹ cần mở" />
            )}
          </Card>
        ) : null}

        <IdentificationPreviewModal
          open={isPreviewModalOpen}
          onClose={() => {
            setIsPreviewModalOpen(false);
            setSelectedImage(null);
          }}
          identification={identification}
          autoKyc={autoKyc}
          selectedImage={selectedImage}
        />
      </div>
    </>
  );
};

export default ItemDetail;

const BusinessIdentificationDetail = ({ userIdentification }) => {
  if (!userIdentification)
    return <Empty description="Chưa có thông tin xác thực" />;

  return (
    <div className="flex flex-col gap-6 ">
      <Descriptions column={1} bordered>
        <DescItem label="Tên doanh nghiệp">
          {userIdentification.full_name}
        </DescItem>
        <DescItem label="Ngày đăng ký lần đầu">
          {userIdentification.date_of_birth}
        </DescItem>
        <DescItem label="Loại căn cước">
          {cardTypeLabel[userIdentification.card_type]}
        </DescItem>
        <DescItem label="Số chứng nhận đăng ký doanh nghiệp / Mã số thuế">
          {userIdentification.id_number}
        </DescItem>
        <DescItem label="Nơi cấp">{userIdentification.issue_place}</DescItem>
        <DescItem label="Trạng thái xác thực">
          <Tag
            color={
              identificationStatusColors[userIdentification.verification_status]
            }
          >
            {userIdentification.verification_status_label}
          </Tag>
        </DescItem>
        <DescItem label="Ghi chú">
          {userIdentification?.identification_logs?.length > 0
            ? userIdentification.identification_logs.map((log, index) => (
                <p key={index}>
                  <Tag color="green">{log.admin_email ?? 'Admin'}</Tag> chuyển
                  sang &nbsp;
                  <Tag
                    color={identificationStatusColors[log.verification_status]}
                  >
                    {log.verification_status_label}
                  </Tag>
                  (Ghi chú:{' '}
                  <UIIconTooltip
                    size="small"
                    title="Edit"
                    Icon={EditOutlined}
                  />{' '}
                  &nbsp;
                  <Tag color="grey">{log.note}</Tag>
                  {formatDateTime(log.created_at)})
                </p>
              ))
            : '-'}
        </DescItem>
      </Descriptions>
      <Descriptions title="Địa chỉ đăng ký kinh doanh" column={1} bordered>
        <DescItem label="Thành phố/Tỉnh">
          {userIdentification.residential_address_city ? (
            <Link
              to={`/investment/provinces/${userIdentification.residential_address_city.id}`}
            >
              {userIdentification.residential_address_city
                ? userIdentification.residential_address_city.name
                : userIdentification.residential_address_city.id}
            </Link>
          ) : userIdentification.residential_address_city_string ? (
            userIdentification.residential_address_city_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Quận/Huyện">
          {userIdentification.residential_address_district ? (
            <Link
              to={`/investment/districts/${userIdentification.residential_address_district.id}`}
            >
              {userIdentification.residential_address_district
                ? userIdentification.residential_address_district.name
                : '-'}
            </Link>
          ) : userIdentification.residential_address_district_string ? (
            userIdentification.residential_address_district_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Địa chỉ">
          {userIdentification.residential_address}
        </DescItem>
      </Descriptions>

      <Descriptions title={'Địa chỉ liên hệ'} column={1} bordered>
        <DescItem label="Thành phố/Tỉnh">
          {userIdentification.address_city ? (
            <Link
              to={`/investment/provinces/${userIdentification.address_city.id}`}
            >
              {userIdentification.address_city.name
                ? userIdentification.address_city.name
                : userIdentification.address_city.id}
            </Link>
          ) : userIdentification.address_city_string ? (
            userIdentification.address_city_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Quận/Huyện">
          {userIdentification.address_district ? (
            <Link
              to={`/investment/districts/${userIdentification.address_district.id}`}
            >
              {userIdentification.address_district_string
                ? userIdentification.address_district_string
                : userIdentification.address_district.id}
            </Link>
          ) : userIdentification.address_district_string ? (
            userIdentification.address_district_string
          ) : (
            '-'
          )}
        </DescItem>
        <DescItem label="Địa chỉ">{userIdentification.address}</DescItem>
      </Descriptions>
    </div>
  );
};

const VerifiedTooltip = () => (
  <Tooltip title="Đã xác thực">
    <CheckCircleOutlined style={{ color: 'green', fontSize: '1.125rem' }} />
  </Tooltip>
);
